import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { OpenInEditorButton } from "@/components/environments/OpenInEditorButton";
import { Button } from "@/components/flexkit/Button";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useGetEditorForEnv } from "@/hooks/use-editor-for-env";
import { useEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { EnvironmentActionsDropdown } from "@/routes/environments/details/EnvironmentActionsDropdown";
import { canOpen } from "@/routes/environments/phase";
import { useEffect, useMemo, useState, type FC } from "react";

export type ActionBarProps = {
    environmentId: string;
    withOpenEditor?: boolean;
};

export const ActionBar: FC<ActionBarProps> = ({ environmentId, withOpenEditor }) => {
    const { data: environment } = useEnvironment(environmentId);

    return (
        <div data-testid="action-bar" translate="no" className="grow self-stretch">
            {environment && <Island environment={environment} withOpenEditor={withOpenEditor} />}
        </div>
    );
};

const Island: FC<
    PropsWithClassName & {
        environment: PlainEnvironment;
        withOpenEditor?: boolean;
    }
> = ({ environment, className, withOpenEditor = true }) => {
    const { selectedEditor } = useGetEditorForEnv(environment.id);

    const [open, setOpen] = useState(false);
    const openable = useMemo(() => {
        return !!environment && canOpen(environment);
    }, [environment]);

    useEffect(() => {
        setOpen((current) => {
            if (current !== openable) {
                return openable;
            }
            return current;
        });
    }, [openable]);

    useEffect(() => {
        setOpen(!!environment && canOpen(environment));
    }, [environment]);

    return (
        <div
            data-testid="action-bar-island"
            className={cn("flex flex-wrap items-stretch justify-center gap-2 @xs:justify-end", className)}
        >
            {open && !window.isWebview && withOpenEditor && <OpenInEditorButton environment={environment} />}

            <EnvironmentActionsDropdown
                environment={environment}
                defaultEditor={selectedEditor}
                button={
                    <Button
                        variant="outline"
                        size="sm"
                        data-testid="open-more-actions"
                        aria-label="More actions"
                        LeadingIcon={IconEllipsisHorizontal}
                    />
                }
            />
        </div>
    );
};
